import { createContext, useContext } from 'react';
import type {
  HeartbeatHandle,
  PlayBgmOptions,
  PlaySfxOptions,
  SfxName,
  StartHeartbeatOptions,
  StopBgmOptions,
} from './audioEngine';

/**
 * useAudio の戻り値（R2 感覚層）。
 *
 * 後続の StartScreen 統合・グローバル音量 UI が「このまま」使う窓口。
 * muted / volume は engine.onChange を購読して再レンダーされる現在値。
 * それ以外は engine の同名メソッドへの薄い委譲（参照は安定）。
 */
export interface AudioApi {
  /** 現在ミュート中か（engine 購読で更新される） */
  muted: boolean;
  /** 現在の master volume（0..1・engine 購読で更新される） */
  volume: number;
  /** AudioContext が unlock 済みで稼働中か */
  isUnlocked: boolean;

  /** ミュートを設定（localStorage 永続） */
  setMuted: (m: boolean) => void;
  /** master volume を設定（0..1・localStorage 永続） */
  setVolume: (v: number) => void;
  /** ミュートを反転 */
  toggleMuted: () => void;
  /** 手動 unlock（通常は Provider が初回ジェスチャで自動実行） */
  unlock: () => void;

  /** 合成 SFX を鳴らす */
  playSfx: (name: SfxName, opts?: PlaySfxOptions) => void;
  /** 鼓動ループを開始しハンドルを返す */
  startHeartbeat: (opts?: StartHeartbeatOptions) => HeartbeatHandle;
  /** BGM をアセット URL から再生 */
  playBgm: (url: string, opts?: PlayBgmOptions) => void;
  /** BGM を停止 */
  stopBgm: (opts?: StopBgmOptions) => void;
  /** mood 連動アンビエント（合成ドローン）を設定 / null で停止 */
  setAmbient: (mood: import('shared').Mood | null) => void;
}

/**
 * AudioApi の React コンテキスト。
 * Provider 外で useAudio を呼んだ場合に分かりやすく失敗させるため、既定は null。
 */
export const AudioContextValue = createContext<AudioApi | null>(null);

/**
 * グローバル音エンジンへの React 窓口。
 * AudioProvider 配下で呼ぶこと（外で呼ぶと throw）。
 */
export function useAudio(): AudioApi {
  const ctx = useContext(AudioContextValue);
  if (!ctx) {
    throw new Error('useAudio must be used within <AudioProvider>');
  }
  return ctx;
}
